/**
 * ============================================================================
 * RFLXNS Theme — Collection Runtime
 * ============================================================================
 * Owns collection-route interactions:
 * - collection header hide-on-scroll
 * - click-only quick add tray state
 * - card-level variant chip selection
 * - two-face gallery toggles
 * - inline size guide visibility
 *
 * This file intentionally does not own the actual add-to-cart request transport.
 * Product forms continue to submit through the shared cart stack.
 * ============================================================================
 */
const initRflxnsCollection = () => {
  const isCollection = document.body.classList.contains('template-collection');

  // --------------------------------------------------------------------------
  // Collection shell motion (Header hide-on-scroll, collection route only)
  // --------------------------------------------------------------------------
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  if (isCollection) {
    let lastScrollY = window.scrollY;
    let ticking = false;

    const updateHeaderState = () => {
      const currentScrollY = window.scrollY;
      const isScrollingDown = currentScrollY > lastScrollY && currentScrollY > 80;
      document.body.classList.toggle('rflxns-header-hidden', isScrollingDown);
      lastScrollY = Math.max(currentScrollY, 0);
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateHeaderState);
      },
      { passive: true }
    );
  }

  /**
   * Open or close the per-card quick add tray by toggling the canonical state
   * class and syncing trigger accessibility state.
   */
  const setQuickAddState = (card, isOpen) => {
    if (!card) return;
    card.classList.toggle('rflxns-quick-add-open', isOpen);
    const trigger = card.querySelector('[data-rflxns-quickadd-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  };

  /**
   * Close every open quick add tray except the current working card.
   */
  const closeOpenQuickAdds = (exceptCard) => {
    document.querySelectorAll('.rflxns-product-card.rflxns-quick-add-open').forEach((card) => {
      if (card !== exceptCard) setQuickAddState(card, false);
    });
  };

  /**
   * Open or close an inline size guide panel.
   */
  const setSizeGuideState = (guide, isOpen) => {
    if (!guide) return;
    guide.classList.toggle('is-open', isOpen);
    const trigger = guide.querySelector('[data-rflxns-size-guide-trigger]');
    if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  };

  /**
   * Close all visible size guides except the one the user is actively using.
   */
  const closeOpenSizeGuides = (exceptGuide) => {
    document.querySelectorAll('.rflxns-product-card__size-guide.is-open').forEach((guide) => {
      if (guide !== exceptGuide) setSizeGuideState(guide, false);
    });
  };

  // --------------------------------------------------------------------------
  // Per-card interactive state
  // --------------------------------------------------------------------------
  const initCard = (card) => {
    if (card.dataset.rflxnsCardInitialized === 'true') return;
    card.dataset.rflxnsCardInitialized = 'true';

    const trigger = card.querySelector('[data-rflxns-quickadd-trigger]');
    const tray = card.querySelector('[data-rflxns-quickadd-tray]');
    const variantInput = card.querySelector('.product-variant-id');
    const variantChips = card.querySelectorAll('[data-rflxns-card-variant-id]');
    const sizeGuide = card.querySelector('.rflxns-product-card__size-guide');
    const sizeGuideTrigger = card.querySelector('[data-rflxns-size-guide-trigger]');
    const sizeGuidePanel = card.querySelector('[data-rflxns-size-guide-panel]');
    const galleryArrows = card.querySelectorAll('[data-rflxns-gallery-arrow]');
    const dots = card.querySelectorAll('[data-rflxns-dot]');
    const stageLink = card.querySelector('.rflxns-product-card__stage-link');
    const stage = card.querySelector('.rflxns-product-card__stage') || stageLink || card.querySelector('.card__media');
    const hasSecondaryMedia =
      card.classList.contains('rflxns-product-card--has-back-face') ||
      dots.length > 0 ||
      (card.querySelector('.second-content img') !== null &&
        !card.querySelector('.second-content')?.classList.contains('rflxns-product-card__panel--single'));

    let openQuickAdd = null;

    if (trigger && tray) {
      let quickAddSuccessTimer;

      const clearQuickAddSuccessTimer = () => {
        if (!quickAddSuccessTimer) return;
        window.clearTimeout(quickAddSuccessTimer);
        quickAddSuccessTimer = null;
      };

      openQuickAdd = () => {
        clearQuickAddSuccessTimer();
        closeOpenQuickAdds(card);
        setQuickAddState(card, true);
      };

      const closeQuickAdd = () => {
        clearQuickAddSuccessTimer();
        card.classList.remove('rflxns-quick-add-success');
        setQuickAddState(card, false);
      };

      const handleQuickAddSuccess = () => {
        openQuickAdd();
        card.classList.add('rflxns-quick-add-success');
        clearQuickAddSuccessTimer();
        quickAddSuccessTimer = window.setTimeout(() => {
          card.classList.remove('rflxns-quick-add-success');
          closeQuickAdd();
          quickAddSuccessTimer = null;
        }, 900);
        closeOpenSizeGuides();
      };

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (card.classList.contains('rflxns-quick-add-open')) {
          closeQuickAdd();
          return;
        }

        openQuickAdd();
      });

      tray.addEventListener('click', (event) => event.stopPropagation());
      tray.addEventListener('focusin', () => {
        if (!card.classList.contains('rflxns-quick-add-open')) return;
        closeOpenQuickAdds(card);
      });

      card.addEventListener('focusout', (event) => {
        if (card.contains(event.relatedTarget)) return;
        closeQuickAdd();
      });

      card.addEventListener('rflxns:quick-add-success', handleQuickAddSuccess);
    }

    if (variantInput && variantChips.length) {
      variantChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          if (chip.disabled || chip.dataset.rflxnsCardVariantAvailable === 'false') return;
          openQuickAdd?.();
          variantInput.value = chip.dataset.rflxnsCardVariantId;
          variantInput.disabled = false;

          variantChips.forEach((candidate) => {
            const isActive = candidate === chip;
            candidate.classList.toggle('is-active', isActive);
            candidate.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          });
        });
      });
    }

    // ------------------------------------------------------------------------
    // Gallery controls: Touch Swipe (Mobile), Dots Pagination & Desktop Arrows
    // ------------------------------------------------------------------------
    if (hasSecondaryMedia || galleryArrows.length || dots.length) {
      /**
       * Toggle between the front and alternate product media panels.
       */
      const setGalleryFace = (showAlt) => {
        card.classList.toggle('rflxns-product-card--show-alt', showAlt);
        galleryArrows.forEach((arrow) => {
          const isActive = arrow.dataset.rflxnsGalleryArrow === (showAlt ? 'next' : 'previous');
          arrow.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        dots.forEach((dot) => {
          const isSecondDot = dot.dataset.rflxnsDot === 'second';
          const isDotActive = showAlt ? isSecondDot : !isSecondDot;
          dot.classList.toggle('is-active', isDotActive);
        });
      };

      // Expose helper on card element for programmatic auto-preview
      card.rflxnsSetGalleryFace = setGalleryFace;
      card.rflxnsIsShowingAlt = () => card.classList.contains('rflxns-product-card--show-alt');

      setGalleryFace(false);

      // Desktop arrow clicks
      galleryArrows.forEach((arrow) => {
        arrow.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          card.dataset.userInteracted = 'true';
          setGalleryFace(arrow.dataset.rflxnsGalleryArrow === 'next');
        });
      });

      // Dots click / tap (instant response on both click & touchend)
      dots.forEach((dot) => {
        const handleDot = (event) => {
          event.preventDefault();
          event.stopPropagation();
          card.dataset.userInteracted = 'true';
          setGalleryFace(dot.dataset.rflxnsDot === 'second');
        };
        dot.addEventListener('click', handleDot);
        dot.addEventListener('touchend', handleDot, { passive: false });
      });

      // ----------------------------------------------------------------------
      // Bulletproof Touch & Pointer Swipe Gestures
      // ----------------------------------------------------------------------
      const swipeTarget = stageLink || stage;
      if (swipeTarget) {
        let touchStartX = 0;
        let touchStartY = 0;
        let touchCurrentX = 0;
        let touchCurrentY = 0;
        let touchStartTime = 0;
        let isHorizontalSwipe = false;
        let suppressClickUntil = 0;
        let lastToggleTimestamp = 0;

        /**
         * Safely toggles to the alternate image face and prevents accidental PDP click navigation.
         */
        const triggerSwipeToggle = () => {
          const now = Date.now();
          if (now - lastToggleTimestamp < 260) return; // Prevent double trigger
          lastToggleTimestamp = now;
          suppressClickUntil = now + 650;
          card.dataset.userInteracted = 'true';

          const currentlyAlt = card.classList.contains('rflxns-product-card--show-alt');
          // Infinite bidirectional flip: cycling to opposite face on each distinct swipe
          setGalleryFace(!currentlyAlt);
        };

        // Touch event listeners (mobile smartphones & tablets)
        swipeTarget.addEventListener(
          'touchstart',
          (e) => {
            if (e.touches && e.touches.length !== 1) return;
            const t = e.touches ? e.touches[0] : e;
            touchStartX = t.clientX;
            touchStartY = t.clientY;
            touchCurrentX = touchStartX;
            touchCurrentY = touchStartY;
            touchStartTime = Date.now();
            isHorizontalSwipe = false;
          },
          { passive: true }
        );

        swipeTarget.addEventListener(
          'touchmove',
          (e) => {
            if (e.touches && e.touches.length !== 1) return;
            const t = e.touches ? e.touches[0] : e;
            touchCurrentX = t.clientX;
            touchCurrentY = t.clientY;
            const deltaX = touchCurrentX - touchStartX;
            const deltaY = touchCurrentY - touchStartY;

            if (!isHorizontalSwipe && Math.abs(deltaX) > 8) {
              if (Math.abs(deltaX) > Math.abs(deltaY)) {
                isHorizontalSwipe = true;
              }
            }
          },
          { passive: true }
        );

        const onTouchGestureEnd = () => {
          const deltaX = touchCurrentX - touchStartX;
          const deltaY = touchCurrentY - touchStartY;
          const duration = Date.now() - touchStartTime;

          // Recognized swipe: >= 16px horizontal movement, predominantly horizontal, < 1200ms
          if (Math.abs(deltaX) >= 16 && (Math.abs(deltaX) > Math.abs(deltaY) || isHorizontalSwipe) && duration < 1200) {
            triggerSwipeToggle();
          }
          isHorizontalSwipe = false;
        };

        swipeTarget.addEventListener('touchend', onTouchGestureEnd, { passive: true });
        swipeTarget.addEventListener('touchcancel', onTouchGestureEnd, { passive: true });

        // Pointer event listeners (supporting trackpads, hybrid laptops, stylus)
        if (window.PointerEvent) {
          let ptrStartX = 0;
          let ptrStartY = 0;
          let ptrCurrentX = 0;
          let ptrCurrentY = 0;
          let ptrStartTime = 0;
          let ptrIsHorizontal = false;

          swipeTarget.addEventListener(
            'pointerdown',
            (e) => {
              if (e.pointerType === 'mouse' && e.button !== 0) return;
              ptrStartX = e.clientX;
              ptrStartY = e.clientY;
              ptrCurrentX = ptrStartX;
              ptrCurrentY = ptrStartY;
              ptrStartTime = Date.now();
              ptrIsHorizontal = false;
            },
            { passive: true }
          );

          swipeTarget.addEventListener(
            'pointermove',
            (e) => {
              ptrCurrentX = e.clientX;
              ptrCurrentY = e.clientY;
              const deltaX = ptrCurrentX - ptrStartX;
              const deltaY = ptrCurrentY - ptrStartY;

              if (!ptrIsHorizontal && Math.abs(deltaX) > 8) {
                if (Math.abs(deltaX) > Math.abs(deltaY)) {
                  ptrIsHorizontal = true;
                }
              }
            },
            { passive: true }
          );

          const onPointerGestureEnd = () => {
            const deltaX = ptrCurrentX - ptrStartX;
            const deltaY = ptrCurrentY - ptrStartY;
            const duration = Date.now() - ptrStartTime;

            if (Math.abs(deltaX) >= 16 && (Math.abs(deltaX) > Math.abs(deltaY) || ptrIsHorizontal) && duration < 1200) {
              triggerSwipeToggle();
            }
            ptrIsHorizontal = false;
          };

          swipeTarget.addEventListener('pointerup', onPointerGestureEnd, { passive: true });
          swipeTarget.addEventListener('pointercancel', onPointerGestureEnd, { passive: true });
        }

        // Intercept link clicks during/after a swipe in the CAPTURE phase so PDP navigation is blocked
        if (stageLink) {
          stageLink.addEventListener(
            'click',
            (event) => {
              if (Date.now() < suppressClickUntil) {
                event.preventDefault();
                event.stopPropagation();
                suppressClickUntil = 0;
                return false;
              }
            },
            true // Capture phase intercepts the event before the link navigates!
          );
        }
      }
    }

    if (sizeGuide && sizeGuideTrigger && sizeGuidePanel) {
      const openSizeGuide = () => {
        closeOpenSizeGuides(sizeGuide);
        setSizeGuideState(sizeGuide, true);
      };

      const closeSizeGuide = () => setSizeGuideState(sizeGuide, false);

      sizeGuide.addEventListener('focusout', (event) => {
        if (sizeGuide.contains(event.relatedTarget)) return;
        closeSizeGuide();
      });
      sizeGuideTrigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const shouldOpen = !sizeGuide.classList.contains('is-open');
        closeOpenSizeGuides(sizeGuide);
        setSizeGuideState(sizeGuide, shouldOpen);
      });
    }
  };

  const initAllCards = (root = document) => {
    root.querySelectorAll('.rflxns-product-card').forEach((card) => {
      initCard(card);
    });
  };

  // Initial card hydration
  initAllCards();

  // Watch for dynamic card rendering (facet filtering, sorting, pagination, infinite scroll)
  if ('MutationObserver' in window) {
    const gridObserver = new MutationObserver((mutations) => {
      let hasNewElements = false;
      for (const mutation of mutations) {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          hasNewElements = true;
          break;
        }
      }
      if (hasNewElements) {
        initAllCards();
      }
    });

    const container =
      document.querySelector('#ProductGridContainer') ||
      document.querySelector('.rflxns-collection-grid-shell') ||
      document.querySelector('main') ||
      document.body;

    if (container) {
      gridObserver.observe(container, { childList: true, subtree: true });
    }
  }

  // --------------------------------------------------------------------------
  // Organic Random Auto-Scroll / Auto-Flip Preview Engine
  // --------------------------------------------------------------------------
  const getMultiCards = () =>
    Array.from(document.querySelectorAll('.rflxns-product-card')).filter((card) => {
      const hasAlt = card.classList.contains('rflxns-product-card--has-back-face');
      const hasDots = card.querySelector('[data-rflxns-dot]') !== null;
      const secondImg = card.querySelector('.second-content img');
      const isSingle = card.querySelector('.second-content')?.classList.contains('rflxns-product-card__panel--single');
      return hasAlt || hasDots || (secondImg && !isSingle);
    });

  const allMultiCards = getMultiCards();

  if (allMultiCards.length > 0) {
    const visibleCardsSet = new Set();
    let isUserInteracting = false;
    let userInteractionCooldownTimer = null;

    const flagUserActivity = () => {
      isUserInteracting = true;
      if (userInteractionCooldownTimer) window.clearTimeout(userInteractionCooldownTimer);
      userInteractionCooldownTimer = window.setTimeout(() => {
        isUserInteracting = false;
      }, 4000);
    };

    window.addEventListener('touchstart', flagUserActivity, { passive: true });
    window.addEventListener('touchmove', flagUserActivity, { passive: true });
    window.addEventListener('wheel', flagUserActivity, { passive: true });

    if ('IntersectionObserver' in window) {
      const cardVisibilityObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              visibleCardsSet.add(entry.target);
            } else {
              visibleCardsSet.delete(entry.target);
            }
          });
        },
        { threshold: 0.3 }
      );

      allMultiCards.forEach((card) => cardVisibilityObserver.observe(card));
    } else {
      allMultiCards.forEach((card) => visibleCardsSet.add(card));
    }

    /**
     * Executes a subtle, organic image preview flip on a random visible product card.
     */
    const triggerRandomCardFlip = () => {
      if (document.hidden || isUserInteracting || visibleCardsSet.size === 0) {
        scheduleNextAutoFlip();
        return;
      }

      const visibleArray = Array.from(visibleCardsSet).filter(
        (card) =>
          typeof card.rflxnsSetGalleryFace === 'function' &&
          !card.matches(':hover') &&
          card.dataset.userInteracted !== 'true'
      );

      if (visibleArray.length > 0) {
        const randomIndex = Math.floor(Math.random() * visibleArray.length);
        const selectedCard = visibleArray[randomIndex];

        const isCurrentlyAlt = selectedCard.rflxnsIsShowingAlt();
        const nextState = !isCurrentlyAlt;

        selectedCard.rflxnsSetGalleryFace(nextState);

        // If flipped to back image, smoothly return to front face after a 3s preview
        if (nextState) {
          window.setTimeout(() => {
            if (
              typeof selectedCard.rflxnsSetGalleryFace === 'function' &&
              !selectedCard.matches(':hover') &&
              selectedCard.dataset.userInteracted !== 'true'
            ) {
              selectedCard.rflxnsSetGalleryFace(false);
            }
          }, 3200);
        }
      }

      scheduleNextAutoFlip();
    };

    const scheduleNextAutoFlip = () => {
      // Random delay between 3.5s and 6.0s for an organic, non-mechanical feel
      const randomDelay = Math.floor(Math.random() * 2500) + 3500;
      window.setTimeout(triggerRandomCardFlip, randomDelay);
    };

    // Initial warm-up delay before starting auto-preview loop
    window.setTimeout(scheduleNextAutoFlip, 3000);
  }

  // --------------------------------------------------------------------------
  // Global dismissal rules
  // --------------------------------------------------------------------------
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.rflxns-product-card.rflxns-quick-add-open, [data-rflxns-quickadd-trigger]')) {
      closeOpenQuickAdds();
    }
    if (!event.target.closest('.rflxns-product-card__size-guide')) {
      closeOpenSizeGuides();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeOpenQuickAdds();
      closeOpenSizeGuides();
    }
  });

  window.addEventListener(
    'scroll',
    () => {
      // Mobile scroll dismisses transient overlays so the catalogue stays readable.
      if (!mobileQuery.matches) return;
      closeOpenQuickAdds();
      closeOpenSizeGuides();
    },
    { passive: true }
  );
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRflxnsCollection);
} else {
  initRflxnsCollection();
}

